import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import ChangePasswordModal from './components/Auth/ChangePasswordModal';
import AppShell, { AppTab } from './components/Layout/AppShell';
import ImpersonationBanner from './components/Admin/ImpersonationBanner';
import DashboardPage from './pages/DashboardPage';
import LeadsPage from './pages/LeadsPage';
import PipelinePage from './pages/PipelinePage';
import TasksPage from './pages/TasksPage';
import SetupPage from './pages/SetupPage';
import LeadDetailModal from './components/LeadDetail/LeadDetailModal';
import AddLeadModal from './components/LeadDetail/AddLeadModal';
import { Lead } from './types/lead';
import { Stage } from './types/stage';
import { Task } from './types/task';
import { Tag } from './types/tag';
import { getLeads, updateLead, deleteLead } from './lib/leadsManagement';
import { getStages } from './lib/stagesManagement';
import { getTasks } from './lib/tasksManagement';
import { getTags, getAllLeadTagsMap } from './lib/tagsManagement';
import { getLatestNotesMap } from './lib/notesManagement';
import { updatePassword, resetUserPassword } from './lib/userManagement';
import UserList from './components/Admin/UserManagement/UserList';

const App: React.FC = () => {
  const {
    isAuthenticated,
    currentUser,
    logout,
    isAdmin,
    isKunde,
    startImpersonation,
    stopImpersonation,
    isImpersonating,
    getEffectiveUser,
    viewingAsUser,
    updateCurrentUser,
  } = useAuth();

  const [activeTab, setActiveTab] = useState<AppTab>('dashboard');

  // Browser Back-Button: Tab-Navigation in History schreiben
  const handleTabChange = (tab: AppTab) => {
    if (tab !== activeTab) {
      history.pushState({ tab }, '', '');
    }
    setActiveTab(tab);
  };

  useEffect(() => {
    // Initialzustand in History setzen
    history.replaceState({ tab: 'dashboard' }, '', '');

    const handlePopState = (e: PopStateEvent) => {
      const tab = e.state?.tab as AppTab | undefined;
      if (tab) {
        setActiveTab(tab);
      } else {
        // Kein State → zurück auf Dashboard statt App verlassen
        history.pushState({ tab: 'dashboard' }, '', '');
        setActiveTab('dashboard');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Redirect Kunde away from restricted tabs
  useEffect(() => {
    if (isKunde() && (activeTab === 'tasks' || activeTab === 'setup')) {
      setActiveTab('dashboard');
    }
  }, [activeTab, currentUser]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [leadTagsMap, setLeadTagsMap] = useState<Map<string, Tag[]>>(new Map());
  const [leadNotesMap, setLeadNotesMap] = useState<Map<string, string>>(new Map());
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);

  // Archived lead IDs (localStorage for now, same as before)
  const [archivedLeadIds, setArchivedLeadIds] = useState<Set<string>>(new Set());

  const isLoadingRef = useRef(false);

  // Load archived IDs from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('archivedLeads');
    if (saved) {
      try { setArchivedLeadIds(new Set(JSON.parse(saved))); } catch {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('archivedLeads', JSON.stringify(Array.from(archivedLeadIds)));
  }, [archivedLeadIds]);

  // Initial data load
  useEffect(() => {
    if (!isAuthenticated || !currentUser || isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLeadsLoading(true);

    const effectiveUser = getEffectiveUser();
    const userIdFilter = (isAdmin() && !isImpersonating()) ? undefined : effectiveUser?.id;

    Promise.all([
      getLeads(userIdFilter),
      getStages(),
      getTasks(userIdFilter ? { createdBy: userIdFilter } : undefined),
      getTags(),
      getLatestNotesMap(),
      getAllLeadTagsMap(),
    ]).then(([leadsData, stagesData, tasksData, tagsData, notesMap, tagsMap]) => {
      setLeads(leadsData);
      setStages(stagesData);
      setTasks(tasksData);
      setTags(tagsData);
      setLeadNotesMap(notesMap);
      setLeadTagsMap(tagsMap);
      setLeadsLoading(false);
      isLoadingRef.current = false;
    }).catch(() => {
      setLeadsLoading(false);
      isLoadingRef.current = false;
    });
  }, [isAuthenticated, currentUser, viewingAsUser]);

  // Derived: active & archived leads
  const activeLeads = useMemo(() =>
    leads.filter(l => !archivedLeadIds.has(l.id) && !l.archived),
    [leads, archivedLeadIds]
  );

  const archivedLeads = useMemo(() =>
    leads.filter(l => archivedLeadIds.has(l.id) || l.archived),
    [leads, archivedLeadIds]
  );

  // Handlers
  const handleLeadCreated = (lead: Lead) => {
    setLeads(prev => [lead, ...prev]);
  };

  const handleUpdateLead = async (updates: Partial<Lead>) => {
    if (!selectedLead) return;
    const updatedLeads = leads.map(l => l.id === selectedLead.id ? { ...l, ...updates } : l);
    setLeads(updatedLeads);
    await updateLead(selectedLead.id, updates);
    // Update selectedLead
    setSelectedLead(prev => prev ? { ...prev, ...updates } : null);
  };

  const handleDeleteLead = async (leadId: string) => {
    await deleteLead(leadId);
    setLeads(prev => prev.filter(l => l.id !== leadId));
    setSelectedLead(null);
  };

  const handleNoteCreated = (leadId: string, content: string) => {
    setLeadNotesMap(prev => new Map(prev).set(leadId, content));
  };

  const handleArchiveLead = (leadId: string) => {
    setArchivedLeadIds(prev => new Set([...prev, leadId]));
    setSelectedLead(null);
  };

  const handleRestoreLead = (leadId: string) => {
    setArchivedLeadIds(prev => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
  };

  const handleDeleteArchivedLead = async (leadId: string) => {
    await deleteLead(leadId);
    setLeads(prev => prev.filter(l => l.id !== leadId));
    setArchivedLeadIds(prev => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
  };

  const handlePasswordChange = async (newPassword: string) => {
    if (!currentUser) return;
    const result = await updatePassword(newPassword);
    if (result.success) {
      await updateCurrentUser({ require_password_change: false });
    } else {
      alert(`Fehler: ${result.error}`);
    }
  };

  const handleResetPassword = async (email: string) => {
    const result = await resetUserPassword(email);
    if (result.success) {
      alert(`Passwort-Reset-Email gesendet an ${email}`);
    } else {
      alert(`Fehler: ${result.error}`);
    }
  };

  // Auth Gates
  if (!isAuthenticated) return <Login />;

  if (currentUser?.require_password_change) {
    return (
      <ChangePasswordModal
        userEmail={currentUser.email}
        onPasswordChanged={handlePasswordChange}
      />
    );
  }

  if (leadsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-zinc-500 font-medium">Lade Daten...</p>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    // Admin ohne aktive Impersonation → immer Userübersicht
    if (isAdmin() && !isImpersonating()) {
      return (
        <div className="px-4 md:px-8 py-6">
          <UserList
            onImpersonate={(userId) => {
              startImpersonation(userId);
              handleTabChange('dashboard');
            }}
            onEditUser={() => {}}
            onCreateUser={() => {}}
            onResetPassword={handleResetPassword}
          />
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardPage
            leads={activeLeads}
            tasks={tasks}
            onTabChange={handleTabChange}
            onLeadClick={setSelectedLead}
            userName={currentUser?.full_name || 'User'}
          />
        );
      case 'leads':
        return (
          <LeadsPage
            leads={activeLeads}
            tags={tags}
            leadTagsMap={leadTagsMap}
            leadNotesMap={leadNotesMap}
            onLeadClick={setSelectedLead}
            onCreateLead={isKunde() ? undefined : () => setShowAddLead(true)}
            onArchiveLead={handleArchiveLead}
            onDeleteLead={handleDeleteLead}
          />
        );
      case 'pipeline':
        return (
          <PipelinePage
            leads={activeLeads}
            stages={stages}
            leadTagsMap={leadTagsMap}
            leadNotesMap={leadNotesMap}
            onLeadClick={setSelectedLead}
            onStagesChange={setStages}
            onLeadsChange={setLeads}
          />
        );
      case 'tasks':
        return (
          <TasksPage
            tasks={tasks}
            leads={activeLeads}
            currentUserId={currentUser?.id}
            onTasksChange={setTasks}
          />
        );
      case 'setup':
        return (
          <SetupPage
            tags={tags}
            onTagsChange={setTags}
            archivedLeads={archivedLeads}
            onRestoreLead={handleRestoreLead}
            onDeleteArchivedLead={handleDeleteArchivedLead}
            isAdmin={isAdmin()}
            onResetPassword={handleResetPassword}
            onImpersonate={(userId) => {
              startImpersonation(userId);
              handleTabChange('dashboard');
            }}
          />
        );
    }
  };

  return (
    <AppShell
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onLogout={logout}
      userName={currentUser?.full_name || 'User'}
      impersonationBanner={
        isImpersonating() && viewingAsUser ? (
          <ImpersonationBanner
            userName={viewingAsUser.full_name}
            onExit={stopImpersonation}
          />
        ) : undefined
      }
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          {renderPage()}
        </motion.div>
      </AnimatePresence>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {selectedLead && (
          <LeadDetailModal
            lead={selectedLead}
            allTags={tags}
            allStages={stages}
            currentUserId={currentUser?.id}
            onClose={() => setSelectedLead(null)}
            onUpdate={handleUpdateLead}
            onDelete={handleDeleteLead}
            onArchive={handleArchiveLead}
            onNoteCreated={handleNoteCreated}
          />
        )}
      </AnimatePresence>

      {/* Add Lead Modal */}
      <AnimatePresence>
        {showAddLead && (
          <AddLeadModal
            onClose={() => setShowAddLead(false)}
            onCreated={handleLeadCreated}
            currentUserId={currentUser?.id}
            defaultStageId={stages.find(s => s.name === 'Neue Anfrage')?.id}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
};

export default App;
